import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import { ticketsAPI } from '../utils/api'
import { formatDate, formatDateTime } from '../utils/helpers'
import './TicketDownload.scss'

const TicketDownload = () => {
  const [searchParams] = useSearchParams()
  const { orderId: paramOrderId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloadLoading, setDownloadLoading] = useState(false)
  const pollingRef = useRef(null)
  const initiatedRef = useRef(false)

  useEffect(() => {
    // Get order details from URL parameters (from payment callback)
    const queryOrderId = searchParams.get('orderId')
    const orderId = queryOrderId || paramOrderId
    const token = searchParams.get('token')
    const status = searchParams.get('status')

    if (initiatedRef.current) return
    initiatedRef.current = true

    if (orderId) {
      loadOrderDetails(orderId, token, status)
    } else {
      // Try to get from sessionStorage as fallback
      const storedOrder = sessionStorage.getItem('ticketOrder')
      if (storedOrder) {
        const order = JSON.parse(storedOrder)
        if (order.orderId) {
          loadOrderDetails(order.orderId, order.orderToken, 'success')
        } else {
          setError(t('download.noOrderFound'))
          setLoading(false)
        }
      } else {
        setError(t('download.noOrderFound'))
        setLoading(false)
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [searchParams, paramOrderId])

  const loadOrderDetails = async (orderId, token, status) => {
    try {
      setLoading(true)
      setError('')
      
      if (status === 'cancelled' || status === 'error') {
        setError(t('download.paymentFailed'))
        setLoading(false)
        return
      }

      const orderDetails = await graphqlAPI.getOrder(orderId)
      setOrderData(orderDetails)

      if (orderDetails && orderDetails.status !== 'PAID') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        pollingRef.current = setInterval(async () => {
          try {
            const latest = await graphqlAPI.getOrder(orderId)
            setOrderData(latest)
            if (latest.status === 'PAID') {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
          } catch (e) {
            // ignore polling errors
          }
        }, 4000)
      }
      
      // Clear session storage as order is complete
      sessionStorage.removeItem('ticketOrder')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadTickets = async () => {
    try {
      setDownloadLoading(true)
      const blobData = await ticketsAPI.getTicketDownloads(orderData.orderId)
      const url = window.URL.createObjectURL(blobData)
      const a = document.createElement('a')
      a.href = url
      a.download = `tickets-${orderData.orderId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => window.URL.revokeObjectURL(url), 2000)
    } catch (err) {
      setError(err.message)
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleNewOrder = () => {
    navigate('/')
  }

  const handleViewHistory = () => {
    const url = orderData?.email
      ? `/#/history?email=${encodeURIComponent(orderData.email)}`
      : '/#/history'
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (loading) {
    return (
      <div className="ticket-download">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-3">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="ticket-download">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="error-card card">
                <div className="card-body text-center">
                  <div className="error-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <h3>{t('download.error')}</h3>
                  <p className="detail-item mb-4 center" style={{ justifyContent: 'center' }}>{error}</p>
                  <button
                    className="btn btn-primary"
                    onClick={handleNewOrder}
                  >
                    {t('download.backToEvents')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isPaid = orderData?.status === 'PAID'
  return (
    <div className="ticket-download">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="success-card card">
              <div className="card-body">
                {/* Header */}
                <div className="success-header text-center mb-4">
                  <div className="success-icon">
                    <i className={isPaid ? 'fas fa-check-circle' : 'fas fa-hourglass-half'}></i>
                  </div>
                  <h2>{isPaid ? t('download.success') : t('download.waitingConfirmationTitle')}</h2>
                  <p>
                    {isPaid ? t('download.successMessage') : t('download.waitingConfirmationMessage')}
                  </p>
                </div>

                <div className="order-details mb-4">
                  <h4>{t('download.eventDetails')}</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">{t('download.event')}:</span>
                      <span className="value">{orderData.eventName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">{t('download.eventDate')}:</span>
                      <span className="value">{formatDate(orderData.eventDate)}</span>
                    </div>
                  </div>
                </div>

                <div className="order-details mb-4">
                  <h4>{t('download.orderDetails')}</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">{t('download.orderNumber')}:</span>
                      <span className="value">{orderData.orderId}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">{t('download.quantity')}:</span>
                      <span className="value">{(orderData.transactionQuantity ?? orderData.quantity)} {(orderData.transactionQuantity ?? orderData.quantity) === 1 ? 'entrada' : 'entradas'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">{t('download.total')}:</span>
                      <span className="value total-amount">{orderData.transactionAmount ?? orderData.total}€</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">{t('download.email')}:</span>
                      <span className="value">{orderData.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">{t('download.purchaseDate')}:</span>
                      <span className="value">{formatDateTime(orderData.transactionCreatedAt || orderData.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Download Section */}
                <div className="download-section mb-4">
                  <h4>{t('download.downloadTitle')}</h4>
                  <p className="mb-3">
                    {isPaid ? t('download.downloadMessage') : t('download.downloadDisabledPending')}
                  </p>
                  
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleDownloadTickets}
                    disabled={downloadLoading || !isPaid}
                  >
                    {downloadLoading ? (
                      <>
                        <span className="spinner-sm me-2"></span>
                        {t('download.downloading')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-2"></i>
                        {t('download.downloadTickets')}
                      </>
                    )}
                  </button>
                </div>

                {/* Important Information */}
                <div className="important-info mb-4">
                  <h5>{t('download.importantInfo')}</h5>
                  <ul>
                    <li>{t('download.bringTickets')}</li>
                    <li>{t('download.arriveEarly')}</li>
                    <li>{t('download.contactSupport')}</li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="form-actions text-center">
                  <button
                    className="btn btn-outline"
                    onClick={handleNewOrder}
                  >
                    {t('download.buyMoreTickets')}
                  </button>
                  <button
                    className="btn btn-outline ms-2"
                    onClick={handleViewHistory}
                  >
                    {t('download.viewHistory')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TicketDownload
